import { createContext, useContext, useEffect, useState } from 'react';
import { PaymentInput, Site } from '../../generated/graphql';
import { SessionInformation } from '../hooks/useStepHandler';
import { t } from 'i18next';
import { useHistory } from 'react-router-dom';
import { ApplePayPaymentResponse } from '../types/apple-pay';

type ApplePayContextType = {
  handleApplePayClick: (
    amount: number,
    site: Site | null,
    sessionInformation: SessionInformation,
    setError: (message: string) => void,
  ) => () => void;
  isApplePayAvailable: boolean;
};

export const ApplePayContext = createContext<ApplePayContextType>({
  handleApplePayClick: () => () => {},
  isApplePayAvailable: false,
});

export const ApplePayProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const history = useHistory();
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);

  const handleApplePayClick =
    (
      amount: number,
      site: Site | null,
      sessionInformation: SessionInformation,
      setError: (message: string) => void,
    ) =>
    () => {
      if (!ApplePaySession) {
        return;
      }

      const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
        countryCode: process.env.REACT_APP_APPLE_PAY_COUNTRY_CODE || 'NZ',
        currencyCode: process.env.REACT_APP_APPLE_PAY_CURRENCY_CODE || 'NZD',
        supportedNetworks:
          process.env.REACT_APP_APPLE_PAY_SUPPORTED_NETWORKS?.split(',') || [
            'visa',
            'masterCard',
            'amex',
          ],
        merchantCapabilities:
          (process.env.REACT_APP_APPLE_PAY_MERCHANT_CAPABILITIES?.split(
            ',',
          ) as ApplePayJS.ApplePayMerchantCapability[]) || ['supports3DS'],
        total: {
          label:
            process.env.REACT_APP_APPLE_PAY_MERCHANT_NAME || 'Central Park',
          amount: amount.toString(),
        },
      };

      const session = new ApplePaySession(3, paymentRequest);

      session.onvalidatemerchant = async (event) => {
        try {
          const response = await fetch(
            (process.env.REACT_APP_PUBLIC_URL || '').replace('/graphql', '') +
              '/api/apple-pay/validate-merchant',
            {
              method: 'POST',
              body: JSON.stringify({ validationUrl: event.validationURL }),
              headers: { 'Content-Type': 'application/json' },
            },
          );
          const { merchantSession } = await response.json();
          session.completeMerchantValidation(merchantSession);
        } catch (error) {
          session.abort();
          setError(t('paymentProcessingError'));
        }
      };

      session.onpaymentauthorized = async (event) => {
        console.log('payment authorized');
        try {
          const paymentInfo: PaymentInput = {
            feeType: sessionInformation.selectedFee,
            storePayment: sessionInformation.checkedCreditCard,
            vrm: sessionInformation.vrm,
            duration: sessionInformation.duration,
            name: sessionInformation.name,
            reachedMaxDuration: sessionInformation.reachedMaxDuration,
            receiveEmails: sessionInformation.receiveEmails,
            receiveSms: sessionInformation.receiveSms,
            email: sessionInformation.email,
            phone: sessionInformation.phone,
            specialTariffPricingId: sessionInformation.specialTariffSelected,
            startTime: sessionInformation.startTime,
            timelineStepAccumulatedCost:
              sessionInformation.timelineStepAccumulatedCost,
            timelineStepDurationMinutes:
              sessionInformation.timelineStepDurationMinutes,
            timelineStepSpecialTariffId:
              sessionInformation.timelineStepSpecialTariffId,
            qrCode: site?.qrCode ?? '',
            topupFixedTime: sessionInformation.topupFixedTime,
            parkingTicketSessionId: sessionInformation.parkingTicketSessionId,
          };
          const response = await fetch(
            (process.env.REACT_APP_PUBLIC_URL || '').replace('/graphql', '') +
              '/api/apple-pay/process-payment',
            {
              method: 'POST',
              body: JSON.stringify({
                token: event.payment.token,
                amount: amount,
                paymentInfo,
              }),
              headers: { 'Content-Type': 'application/json' },
            },
          );
          const { success, data, error } =
            (await response.json()) as ApplePayPaymentResponse;

          if (success) {
            setError('');
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            history.push(`/confirmation/${data.particular}`);
          } else {
            setError(t('paymentProcessingError'));
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            history.push(`/failed?${error?.message}`);
          }
        } catch (error) {
          setError(t('paymentProcessingError'));
          session.completePayment(ApplePaySession.STATUS_FAILURE);
        }
      };

      session.begin();
    };

  useEffect(() => {
    if (window.location.protocol !== 'https:') {
      console.log('Apple Pay is not available');
      setIsApplePayAvailable(false);
      return;
    }
    const script = document.createElement('script');
    script.src =
      'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    script.async = true;
    document.body.appendChild(script);
    setIsApplePayAvailable(true);
    return () => {
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <ApplePayContext.Provider
      value={{ handleApplePayClick, isApplePayAvailable }}
    >
      {children}
    </ApplePayContext.Provider>
  );
};

export const useApplePay = () => {
  return useContext(ApplePayContext);
};
